import type { ImportJobDto } from "../../shared/api-types";

// Time left for an import in progress (owner, 2026-10-22: without an estimate people think it froze and leave).
// Learned from this import's own pages: the average time of the pages already read; before the first one, the
// time measured on real phone photos. Pages are read a few at a time, so the remaining work is shared out.

/** One photographed page, read three ways (whole page + 4 zones in full and for prices): measured ≈ 45–60 s. */
export const DEFAULT_PAGE_MS = 50_000;
/** Pages read at the same time (queue consumer concurrency, wrangler.jsonc). */
const PARALLEL_PAGES = 2;
/** Building the draft after the last page. */
const MERGE_MS = 5_000;

export interface ImportEta {
  done: number;
  total: number;
  /** Pages being read now. */
  reading: number;
  remainingMs: number;
}

const FINISHED = new Set(["done", "failed", "blocked"]);

export function importEta(job: Pick<ImportJobDto, "status" | "files" | "menuId">, now: number): ImportEta {
  const total = job.files.length;
  const done = job.files.filter((f) => FINISHED.has(f.status)).length;
  const reading = job.files.filter((f) => f.status === "processing").length;
  const durations = job.files
    .filter(
      (f) =>
        FINISHED.has(f.status) && f.startedAt != null && f.finishedAt != null && f.finishedAt > f.startedAt,
    )
    .map((f) => (f.finishedAt as number) - (f.startedAt as number));
  const perPage = durations.length
    ? durations.reduce((a, b) => a + b, 0) / durations.length
    : DEFAULT_PAGE_MS;
  // A page being read needs what is left of an average page (never less than a few seconds: it is not done).
  const inProgress = job.files
    .filter((f) => f.status === "processing")
    .reduce(
      (sum, f) => sum + Math.max(perPage * 0.15, perPage - (f.startedAt != null ? now - f.startedAt : 0)),
      0,
    );
  const waiting = job.files.filter((f) => f.status === "pending").length * perPage;
  const parallel = Math.max(PARALLEL_PAGES, reading);
  const merging = done === total && total > 0 && !job.menuId ? MERGE_MS : 0;
  const remainingMs =
    job.status === "ready" && job.menuId
      ? 0
      : Math.round((inProgress + waiting) / parallel) + (done < total ? MERGE_MS : merging);
  return { done, total, reading, remainingMs };
}

/** "mai durează sub un minut", "mai durează ~3 min" (short enough for a phone). */
export function etaText(ms: number): string {
  if (ms < 60_000) return "mai durează sub un minut";
  return `mai durează ~${Math.ceil(ms / 60_000)} min`;
}
